import { motion } from "motion/react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ComingSoonPanelProps {
  /** Heading. Falls back to the generic "coming soon" title. */
  title?: string;
  /** Supporting line under the badge. Falls back to the generic blurb. */
  blurb?: string;
}

/**
 * The coming-soon visual, with no router dependency (#1585 D1).
 *
 * Two entry points share this one definition: `pages/ComingSoon.tsx`, which
 * resolves its copy from the current route, and in-module feature stubs such
 * as the email Settings gate, which have no route to resolve from (Settings is
 * `isSettingsOpen` state, not a path).
 */
export function ComingSoonPanel({ title, blurb }: ComingSoonPanelProps) {
  const { t } = useTranslation();

  const heading = title ?? t("comingSoon.fallbackTitle");
  const sub = blurb ?? t("comingSoon.defaultBlurb");

  return (
    <div className="flex-1 flex items-center justify-center p-10 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="text-center space-y-6 max-w-md"
      >
        <div className="w-20 h-20 bg-primary/10 rounded-3xl mx-auto flex items-center justify-center">
          <Sparkles className="size-9 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-3xl font-black tracking-tight text-foreground">{heading}</h2>
          <p className="text-[10px] font-black uppercase tracking-widest text-primary">{t("comingSoon.badge")}</p>
        </div>
        <p className="text-muted-foreground font-medium leading-relaxed">{sub}</p>
      </motion.div>
    </div>
  );
}
